import axios from 'axios'
import {
    ApplePayTokenizer,
    ApplePayDeferredPaymentRequest,
    ApplePayRecurringPaymentRequest,
} from '../components/stepper/DPaymentMethod'
import {logger} from '../common/Logger'

const API_VERSION = 4
const RECURRING_API_VERSION = 12
const DEFERRED_API_VERSION = 14

// Merchant-validation POST timeout. Bound locked to [8s, 15s] (approval.md lock 11);
// 10s chosen mid-range and deliberately distinct from ApiClient's 30000ms default so
// this bound reads as the intentional Apple-Pay one, not an inherited default.
const VALIDATION_TIMEOUT_MS = 10000

// Session-lifecycle state machine — normative spec v2 (eng-review.md § state machine).
//
//         IDLE ──tap──▶ SESSION_LIVE
//                         · static ApplePayClient.activeSession set (dup-guard: a
//                           second startPayment while one is live is a no-op — no
//                           second begin(); PaymentMethodSelector also disables the button)
//                         · skipManualToken NOT set here — set at authorization, inside
//                           handleApplePayCheckout (DPaymentMethod), today's behavior, kept
//           │
//           ├─ VALIDATION_ABORT  (validation POST fails non-timeout: abort() + error path)
//           ├─ TIMED_OUT         (validation POST times out — ECONNABORTED — our abort();
//           │                     abortedByUs set so a WebKit oncancel afterwards is swallowed)
//           ├─ CANCELLED         (genuine user oncancel: quiet return, no toast)
//           └─ AUTHORIZED        (authorizePaymentOnServer runs; still SESSION_LIVE for the gate)
//                  ├─ COMPLETED  (postback ok → completePayment(SUCCESS) → resolve)
//                  └─ FAILED     (authz false / postback throw / ANY wrapped session-API throw
//                                 → completePayment(FAILURE) attempt → typed reject)
//
// HARDENING RULES:
// · settle() is the single settlement chokepoint — idempotent (no-op once terminal),
//   so it is both the "exactly one terminal" guarantee and the post-await gate for the promise.
// · every post-await session call additionally checks state === SESSION_LIVE before touching
//   the session — a response arriving after a terminal is dropped silently (never calls a
//   session method on an aborted/completed session).
// · every session API call (completeMerchantValidation / completePayment / abort / begin) is
//   wrapped; a throw routes to the FAILED terminal — settlement is UNCONDITIONAL.
// · every terminal clears the static activeSession guard (via settle()).

type ApplePayJsRequest = {
    countryCode: string
    currencyCode: string
    supportedNetworks: string[]
    merchantCapabilities: string[]
    requiredBillingContactFields: string[]
    total: { label: string; amount: string }
    deferredPaymentRequest?: ApplePayDeferredPaymentRequest
    recurringPaymentRequest?: ApplePayRecurringPaymentRequest
}

export type ApplePaySettlementReason = 'failed' | 'cancelled' | 'timed-out' | 'validation-abort'

export class ApplePaySettlementError extends Error {
    constructor(public readonly reason: ApplePaySettlementReason) {
        super(`Apple Pay session settled: ${reason}`)
        this.name = 'ApplePaySettlementError'
    }
}

type SessionState =
    | 'SESSION_LIVE'
    | 'COMPLETED'
    | 'FAILED'
    | 'CANCELLED'
    | 'TIMED_OUT'
    | 'VALIDATION_ABORT'

// Terminal reason → terminal state. Keyed map (over a nested ternary) so adding a reason
// is a compile error here until the state is named.
const TERMINAL_STATE: Record<ApplePaySettlementReason, SessionState> = {
    'failed': 'FAILED',
    'cancelled': 'CANCELLED',
    'timed-out': 'TIMED_OUT',
    'validation-abort': 'VALIDATION_ABORT',
}

export class ApplePayClient {
    // One live session per page (Apple's rule); the dup-guard lives with the session.
    private static activeSession: ApplePayClient | null = null

    private session: any
    private state: SessionState = 'SESSION_LIVE'
    private abortedByUs = false
    private resolve!: () => void
    private reject!: (reason: ApplePaySettlementError) => void

    static canMakePayments(): Promise<boolean> {
        return Promise.resolve(!!(window as any).ApplePaySession?.canMakePayments())
    }

    static startPayment(
        tokenizer: ApplePayTokenizer,
        {label, amount}: {label: string; amount: string},
        authorizePaymentOnServer: (payment: any) => Promise<boolean>
    ): Promise<void> {
        // Duplicate-session guard: a session is already live — no-op (no second begin()).
        // The UI disables the button during a session, so this backstops non-UI callers.
        if (ApplePayClient.activeSession) {
            return Promise.reject(new ApplePaySettlementError('failed'))
        }

        const client = new ApplePayClient()
        return client.run(tokenizer, {label, amount}, authorizePaymentOnServer)
    }

    private run(
        tokenizer: ApplePayTokenizer,
        {label, amount}: {label: string; amount: string},
        authorizePaymentOnServer: (payment: any) => Promise<boolean>
    ): Promise<void> {
        ApplePayClient.activeSession = this
        this.state = 'SESSION_LIVE'

        const paymentRequest: ApplePayJsRequest = {
            countryCode: tokenizer.countryCode,
            currencyCode: tokenizer.currencyCode,
            supportedNetworks: tokenizer.supportedNetworks,
            merchantCapabilities: tokenizer.merchantCapabilities,
            requiredBillingContactFields: ['postalAddress', 'name', 'email'],
            total: { label, amount },
        }

        // Scheduled/future-dated payments: the backend attaches deferredPaymentRequest
        // (single future charge) or recurringPaymentRequest (installment/auto-pay series)
        // to the tokenizer once it knows the payment isn't same-day (CyberSource KA-04318 —
        // device-generated tokens stopped covering Standing Instruction charges 2025-07-30).
        // Only request the field when the client can actually support it; a same-day
        // checkout never sets either field on the tokenizer, so it's never affected here.
        const supportsVersion = (v: number): boolean =>
            !!(window as any).ApplePaySession?.supportsVersion?.(v)

        // Apple treats deferredPaymentRequest and recurringPaymentRequest as mutually
        // exclusive; if a tokenizer somehow carries both (illegal per Apple's spec),
        // deferred wins.
        let apiVersion = API_VERSION
        if (tokenizer.deferredPaymentRequest && supportsVersion(DEFERRED_API_VERSION)) {
            apiVersion = DEFERRED_API_VERSION
            paymentRequest.deferredPaymentRequest = tokenizer.deferredPaymentRequest
        } else if (tokenizer.recurringPaymentRequest && supportsVersion(RECURRING_API_VERSION)) {
            apiVersion = RECURRING_API_VERSION
            paymentRequest.recurringPaymentRequest = tokenizer.recurringPaymentRequest
        }

        return new Promise<void>((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject

            try {
                this.session = new (window as any).ApplePaySession(apiVersion, paymentRequest)
            } catch (e) {
                this.settle('failed')
                return
            }

            this.session.onvalidatemerchant = async (event: any) => {
                logger.breadcrumb('apple-pay.validation-requested')
                try {
                    const {validationURL} = event
                    const {data: merchantSession} = await axios.post(
                        tokenizer.postbackValidationURL,
                        {
                            validationURL,
                            origin: window.location.origin.replace(/^https?:\/\//, ''),
                        },
                        { timeout: VALIDATION_TIMEOUT_MS }
                    )
                    // SESSION_LIVE gate: a validation response that resolves after the session
                    // already reached a terminal (e.g. the user cancelled mid-await) is dropped —
                    // calling completeMerchantValidation on a dead session throws InvalidAccessError.
                    if (this.state !== 'SESSION_LIVE') return
                    logger.breadcrumb('apple-pay.validated', merchantSession)
                    // Only pass Apple's expected fields — extra fields (e.g. pspId, retries)
                    // added by the backend can cause the signature check to fail silently
                    const {
                        epochTimestamp, expiresAt, merchantSessionIdentifier,
                        nonce, merchantIdentifier, domainName, displayName,
                        signature, operationalAnalyticsIdentifier
                    } = merchantSession
                    const cleanSession: any = {
                        epochTimestamp, expiresAt, merchantSessionIdentifier,
                        nonce, merchantIdentifier, domainName, displayName,
                        signature, operationalAnalyticsIdentifier
                    }
                    this.wrap(() => this.session.completeMerchantValidation(cleanSession))
                } catch (e: unknown) {
                    logger.error('apple-pay.validation-requested', e)
                    // A rejection arriving after a terminal (user already cancelled) is dropped.
                    if (this.state !== 'SESSION_LIVE') return
                    this.abortedByUs = true
                    this.wrap(() => this.session.abort())
                    const timedOut = typeof e === 'object' && e !== null
                        && (e as { code?: string }).code === 'ECONNABORTED'
                    if (timedOut) logger.breadcrumb('apple-pay.timed-out')
                    this.settle(timedOut ? 'timed-out' : 'validation-abort')
                }
            }

            // Called even after onpaymentauthorized (per Apple's oncancel docs): both the user
            // and the page can dismiss the sheet. abortedByUs distinguishes our own abort()
            // (timeout / validation failure) from a genuine user cancel.
            this.session.oncancel = () => {
                if (this.abortedByUs) return
                logger.breadcrumb('apple-pay.cancelled')
                this.settle('cancelled')
            }

            //This is actually 'on user approved' as this callback is called when the user
            //validates biometrically but it still needs to go through the server
            this.session.onpaymentauthorized = (event: any) => {
                logger.breadcrumb('apple-pay.authorized')
                const ApplePaySession = (window as any).ApplePaySession
                let authorization: Promise<boolean>
                try {
                    // A synchronous throw here (e.g. a null-assertion in the authorize callback)
                    // would strand the promise — route it to FAILED like any other session failure.
                    authorization = authorizePaymentOnServer(event.payment)
                } catch (e) {
                    logger.error('apple-pay.failed', e)
                    this.wrap(() => this.session.completePayment(ApplePaySession.STATUS_FAILURE))
                    this.settle('failed')
                    return
                }
                authorization
                    .then((success) => {
                        if (this.state !== 'SESSION_LIVE') return
                        if (success) {
                            logger.breadcrumb('apple-pay.completed')
                            this.wrap(() => this.session.completePayment(ApplePaySession.STATUS_SUCCESS))
                            this.settle()
                        } else {
                            logger.breadcrumb('apple-pay.failed')
                            this.wrap(() => this.session.completePayment(ApplePaySession.STATUS_FAILURE))
                            this.settle('failed')
                        }
                    })
                    .catch((e) => {
                        if (this.state !== 'SESSION_LIVE') return
                        logger.error('apple-pay.failed', e)
                        this.wrap(() => this.session.completePayment(ApplePaySession.STATUS_FAILURE))
                        this.settle('failed')
                    })
            }

            this.wrap(() => this.session.begin())
        })
    }

    // Wraps a session API call so a throw (e.g. InvalidAccessError on a dead session) routes
    // to the FAILED terminal instead of stranding the promise.
    private wrap(fn: () => void): void {
        try {
            fn()
        } catch (e) {
            this.settle('failed')
        }
    }

    // The single settlement chokepoint. Idempotent: once the session is terminal, further
    // calls are no-ops — this guarantees exactly one terminal and drops any late settlement.
    // Resolves on completed (no reason); rejects with a typed reason otherwise.
    private settle(reason?: ApplePaySettlementReason): void {
        if (this.state !== 'SESSION_LIVE') return
        this.state = reason ? TERMINAL_STATE[reason] : 'COMPLETED'
        if (ApplePayClient.activeSession === this) {
            ApplePayClient.activeSession = null
        }
        if (reason) {
            this.reject(new ApplePaySettlementError(reason))
        } else {
            this.resolve()
        }
    }
}
